import { z } from 'zod';

/**
 * A generative-dashboard snapshot: OpenUI Lang markup with the data baked in as
 * literals (no client-side fetch, no connector credentials — see
 * docs/generative-dashboards.md invariants 3 and 13). The agent emits this in a
 * single `dashboard` SSE event; the frontend renders it statically.
 *
 * This is the wire/storage envelope shared by the `dashboard` SSE event, the
 * persisted `messages.dashboard_spec` column, and saved dashboard panels. The
 * `format`/`version` pair versions the envelope so a future format can be added
 * without breaking older clients. `doc` is opaque OpenUI Lang text — only the
 * OpenUI runtime parses it; nothing here validates its grammar.
 */
export interface OpenUiDashboardSnapshot {
  format: 'openui-lang';
  version: 1;
  /**
   * Whether this is a saveable data dashboard or a transient inline rich-UI element (comparison,
   * steps, form, callout, one-off table). Absent on snapshots emitted before this field — treated as
   * 'dashboard'. Drives whether the "Save" affordance is offered.
   */
  kind?: 'dashboard' | 'inline';
  /** Optional human-readable title for the panel/card chrome. */
  title?: string;
  /** OpenUI Lang source with embedded literal data. */
  doc: string;
  /**
   * Optional cache of a LIVE dashboard's last query rows, keyed `op::stableStringify(params)`. Written
   * on a live refresh so a `manual` panel renders its last data on reload instead of re-querying.
   * Absent on inline/static snapshots and on docs that have never been refreshed.
   */
  data?: Record<string, unknown[]>;
  /**
   * The connectors the agent's data came from. `[]` means the agent made the data without any, so its
   * refresh runs without connectors; absent on older snapshots, which refresh as before.
   */
  sources?: string[];
  /**
   * The saved dashboard this snapshot was written to, set when the agent updated a saved dashboard
   * from chat. Snake_case because it is the envelope's wire shape as the message stores it. Absent on
   * a dashboard the chat only previewed.
   */
  saved?: SavedDashboardRef;
}

/** A saved dashboard an assistant turn updated, and the version that update made. */
export interface SavedDashboardRef {
  dashboard_id: string;
  name: string;
  revision: number;
}

/** A dashboard snapshot of this message saved to Dashboards, as the message view reports it. */
export interface DashboardLink {
  specIndex: number;
  dashboardId: string;
}

/** The saved-dashboard reference of an envelope; a malformed one is dropped, not fatal. */
const savedRefSchema = z
  .object({
    dashboard_id: z.string().uuid(),
    name: z.string().min(1),
    revision: z.number().int().positive(),
  })
  .optional()
  .catch(undefined);

/**
 * Zod schema for the OpenUI envelope only. We deliberately do NOT validate the
 * OpenUI Lang grammar of `doc` — that is the renderer's job, and invalid markup
 * degrades to a fallback at render time (invariant 6) rather than being rejected
 * here. Keeping `doc` an opaque non-empty string means a malformed-but-present
 * document still reaches the renderer (which surfaces a graceful fallback)
 * instead of silently disappearing upstream.
 */
const openUiSnapshotSchema: z.ZodType<
  OpenUiDashboardSnapshot,
  z.ZodTypeDef,
  unknown
> = z.object({
  format: z.literal('openui-lang'),
  version: z.literal(1),
  kind: z.enum(['dashboard', 'inline']).optional(),
  title: z.string().optional(),
  /**
   * Blank or whitespace-only markup is rejected so a present-but-empty snapshot does not read as
   * "hasDashboard" yet render nothing. The non-blank `doc` stays opaque OpenUI Lang: grammar errors
   * degrade at render time (invariant 6).
   */
  doc: z
    .string()
    .refine((s) => s.trim().length > 0, { message: 'doc must not be blank' }),
  data: z.record(z.string(), z.array(z.unknown())).optional(),
  sources: z.array(z.string()).optional(),
  saved: savedRefSchema,
});

/** Longest HTML document an envelope may carry, in characters. */
export const HTML_DOC_MAX_CHARS = 400_000;

/** Longest serialized `data` map an HTML envelope may carry, in characters. */
export const HTML_DATA_MAX_CHARS = 1_500_000;

/** Metadata of one dataset of an HTML dashboard; only `name` is checked client-side. */
export interface DatasetDescriptor {
  name: string;
  [field: string]: unknown;
}

/** A designed HTML dashboard rendered in a sandboxed frame. See the spec, "Envelope and data model". */
export interface HtmlDashboardSnapshot {
  format: 'html';
  version: 1;
  kind: 'dashboard';
  title?: string;
  doc: string;
  data: Record<string, unknown[]>;
  datasets: DatasetDescriptor[];
  saved?: SavedDashboardRef;
}

/** Every envelope format the SPA renders. */
export type DashboardSnapshot = OpenUiDashboardSnapshot | HtmlDashboardSnapshot;

const htmlSnapshotSchema: z.ZodType<
  HtmlDashboardSnapshot,
  z.ZodTypeDef,
  unknown
> = z
  .object({
    format: z.literal('html'),
    version: z.literal(1),
    kind: z.unknown().transform((): 'dashboard' => 'dashboard'),
    title: z.string().optional(),
    doc: z
      .string()
      .max(HTML_DOC_MAX_CHARS)
      .refine((value) => value.trim().length > 0),
    data: z.record(z.string(), z.array(z.unknown())),
    datasets: z.array(
      z
        .object({ name: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/) })
        .passthrough(),
    ),
    saved: savedRefSchema,
  })
  .refine((value) => JSON.stringify(value.data).length <= HTML_DATA_MAX_CHARS)
  .refine((value) => {
    const names = value.datasets.map((dataset) => dataset.name);
    return (
      new Set(names).size === names.length &&
      Object.keys(value.data).every((name) => names.includes(name))
    );
  });

/** The envelope schema: an HTML envelope or the OpenUI one, chosen by `format`. */
export const dashboardSnapshotSchema: z.ZodType<
  DashboardSnapshot,
  z.ZodTypeDef,
  unknown
> = z.union([htmlSnapshotSchema, openUiSnapshotSchema]);

/** True for a designed HTML dashboard, false for OpenUI. */
export function isHtmlSnapshot(
  snapshot: DashboardSnapshot,
): snapshot is HtmlDashboardSnapshot {
  return snapshot.format === 'html';
}

/** True for an HTML dashboard with no datasets: its numbers are fixed in the page and nothing refreshes them. */
export function isStaticSnapshot(snapshot: DashboardSnapshot): boolean {
  return isHtmlSnapshot(snapshot) && snapshot.datasets.length === 0;
}

const dashboardLinkSchema = z.object({
  specIndex: z.number().int().nonnegative(),
  dashboardId: z.string().min(1),
});

/** The well-formed links of a stored list. An absent or non-array field means none. */
export function parseDashboardLinks(raw: unknown): DashboardLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const result = dashboardLinkSchema.safeParse(entry);
    return result.success ? [result.data] : [];
  });
}

/** True unless the snapshot is explicitly an inline (transient, non-saveable) UI element. */
export function isSaveableDashboard(snapshot: DashboardSnapshot): boolean {
  return snapshot.kind !== 'inline';
}

/**
 * Parse an unknown value (e.g. a deserialized SSE payload or a persisted DB
 * column) into a {@link DashboardSnapshot}, returning `null` when it does not
 * match the envelope. Callers treat `null` as "no dashboard" and fall back to
 * markdown (invariants 4 and 6).
 */
export function parseDashboardSnapshot(raw: unknown): DashboardSnapshot | null {
  const result = dashboardSnapshotSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * True when a dashboard `doc` carries at least one OpenUI `Query()` binding — i.e. it is a live
 * (Variant B) dashboard whose data the runner resolves. A static (Variant A) doc bakes data in as
 * literals and contains no `Query(`.
 */
export function isLiveDashboard(doc: string): boolean {
  return /\bQuery\s*\(/.test(doc);
}

/** FNV-1a 32-bit hash of a string, returned as an unsigned hex string. Deterministic and collision-cheap
 *  enough to key a handful of dashboard cards in one turn by their content. */
function hashString(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Stable React keys for a turn's dashboard cards, derived from each snapshot's content (kind + title +
 * doc) instead of the array index. Index keys swap card identity when `dashboardSpecs` grows or reorders
 * mid-stream, which remounts a card and resets its in-memory answer lock (an answered exam option goes
 * clickable again) or makes it flicker/vanish. Identical cards in one turn are disambiguated with an
 * occurrence suffix so keys stay unique.
 */
export function dashboardCardKeys(
  snapshots: readonly DashboardSnapshot[],
): string[] {
  const seen = new Map<string, number>();
  return snapshots.map((snapshot) => {
    const base = hashString(
      `${snapshot.kind ?? 'dashboard'} ${snapshot.title ?? ''} ${snapshot.doc}`,
    );
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return occurrence === 0 ? base : `${base}#${occurrence}`;
  });
}
